import { useCallback, useMemo } from 'react';
import { Element } from '@thive/canvas';
import { AreaConnection, InterAreaConnection } from '@thive/fddm-types';

import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useCanvasStore } from '@/modules/canvas/store';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { isAnyVehicleOmni, getAreaCrabSetting } from '../factories/connection';

export const useConnectionClick = () => {
  const {
    getAreaOrSegmentById,
    interAreaConnectionActions: { getBetween },
  } = useFloorplanDesignStore.getState();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const getDefaultConnection = useCallback(
    (
      areaIdA: string,
      areaIdB: string,
      direction: Extract<keyof InterAreaConnection, 'aToBConnections' | 'bToAConnections'>,
    ): AreaConnection => {
      const areaOrSegmentA = getAreaOrSegmentById(areaIdA);
      const areaOrSegmentB = getAreaOrSegmentById(areaIdB);
      const bothHighwaySegments =
        areaOrSegmentA?.type === 'segment' && areaOrSegmentB?.type === 'segment';
      const hasCrab =
        getAreaCrabSetting(areaOrSegmentA) !== 0 || getAreaCrabSetting(areaOrSegmentB) !== 0;
      const smoothHeading = hasCrab ? true : isAnyVehicleOmni();

      if (bothHighwaySegments) {
        return {
          smoothHeading,
          connectionType: 'CO_FLOW',
          driveDirection: 'FORWARD',
          trackType: 'CURVED',
        };
      }

      if (direction === 'bToAConnections') {
        return {
          smoothHeading,
          connectionType: 'DRIVE_OUT',
          driveDirection: 'FORWARD',
          trackType: 'CURVED',
        };
      }

      return {
        smoothHeading,
        connectionType: 'DRIVE_IN',
        driveDirection: 'REVERSE',
        trackType: 'CURVED',
      };
    },
    [getAreaOrSegmentById],
  );

  const toggleConnection = useCallback(
    (
      connection: InterAreaConnection,
      areaIdA: string,
      areaIdB: string,
      enable: boolean,
      direction: Extract<keyof InterAreaConnection, 'aToBConnections' | 'bToAConnections'>,
    ) => {
      const updatedConnection = {
        ...connection,
        [direction]: enable ? [getDefaultConnection(areaIdA, areaIdB, direction)] : [],
      };
      fddmSvc.updateInterAreaConnections([updatedConnection]);
    },
    [fddmSvc, getDefaultConnection],
  );

  const connectionClick = useCallback(
    (element: Element) => {
      const canvas = useCanvasStore.getState()?.instance;
      if (!canvas) return;

      if (!element.tags) return;

      const areaIds = element.tags.filter(
        (tag) =>
          tag !== FDDM_TAG.INTER_AREA_CONNECTION &&
          tag !== FDDM_TAG.A_TO_B_CONNECTION &&
          tag !== FDDM_TAG.B_TO_A_CONNECTION &&
          tag !== FDDM_TAG.BUBBLE &&
          tag !== FDDM_TAG.DISTANT_CONNECTION &&
          tag !== FDDM_TAG.LAYER_CONNECTIONS &&
          tag !== FDDM_TAG.LAYER_CONNECTION_BUBBLES,
      );
      if (areaIds.length !== 2) return;

      const interAreaConnection = getBetween(areaIds[0], areaIds[1]);
      if (!interAreaConnection) return;

      if (element.tags?.includes(FDDM_TAG.A_TO_B_CONNECTION)) {
        if (interAreaConnection?.aToBConnections.length === 0) {
          toggleConnection(interAreaConnection, areaIds[0], areaIds[1], true, 'aToBConnections');
        } else {
          toggleConnection(interAreaConnection, areaIds[0], areaIds[1], false, 'aToBConnections');
        }
      } else if (element.tags?.includes(FDDM_TAG.B_TO_A_CONNECTION)) {
        if (interAreaConnection?.bToAConnections.length === 0) {
          toggleConnection(interAreaConnection, areaIds[0], areaIds[1], true, 'bToAConnections');
        } else {
          toggleConnection(interAreaConnection, areaIds[0], areaIds[1], false, 'bToAConnections');
        }
      }
    },
    [getBetween, toggleConnection],
  );

  return { connectionClick };
};
